import { AccountSafetyBoundary } from '../components/AccountSafety';
import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import {
  buildAuthHeaders,
  clearAllAuthData,
  fetchCsrfToken,
  fetchCurrentUser,
  saveUserInfo
} from '../utils/auth';
// eslint-disable-next-line no-unused-vars
import { getCurrentTheme, changeTheme, initializeTheme, THEME_TYPES } from '../utils/theme';

// 사용자 정보를 담을 컨텍스트 생성
export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [userInfo, setUserInfo] = useState(null);
  const [isUserLoading, setIsUserLoading] = useState(true);
  const [userTheme, setUserTheme] = useState(() => getCurrentTheme()); // 테마 상태
  const isValidatingRef = useRef(false);
  const userInfoRef = useRef(null);

  userInfoRef.current = userInfo;

  const clearUserSession = useCallback(() => {
    clearAllAuthData();
    setUserInfo(null);
  }, []);

  const isRetryableValidationStatus = useCallback((status) => {
    return status === 0 || status === 429 || status >= 500;
  }, []);

  // 서버에서 토큰 검증 및 사용자 정보 가져오기
  const validateToken = useCallback(async (retryCount = 0) => {
    if (isValidatingRef.current) {
      return;
    }

    isValidatingRef.current = true;

    try {
      const result = await fetchCurrentUser();

      if (result.ok) {
        setUserInfo(result.user);
      } else if (result.status === 401) {
        clearUserSession();
      } else {
        // 일시적인 서버 오류/레이트리밋은 재시도 후 기존 세션 유지
        if (retryCount < 2 && isRetryableValidationStatus(result.status)) {
          setTimeout(() => {
            validateToken(retryCount + 1);
          }, result.status === 429 ? 3000 : 2000);
          return;
        }

        if (isRetryableValidationStatus(result.status) && userInfoRef.current) {
          return;
        }

        clearUserSession();
      }
    } catch (error) {
      // 네트워크 오류인 경우 재시도 후 기존 세션 유지
      if (retryCount < 2 && (error.name === 'TypeError' || error.message.includes('fetch'))) {
        setTimeout(() => {
          validateToken(retryCount + 1);
        }, 2000);
        return;
      }

      if (userInfoRef.current) {
        return;
      }

      clearUserSession();
    } finally {
      setIsUserLoading(false);
      isValidatingRef.current = false;
    }
  }, [clearUserSession, isRetryableValidationStatus]);

  // 언어 설정 변경 함수
  const updateLanguage = async (newLanguage) => {
    try {
      const response = await fetch('/api/users/language', {
        method: 'PUT',
        headers: buildAuthHeaders({
          'Content-Type': 'application/json'
        }),
        credentials: 'include',
        body: JSON.stringify({
          preferred_language: newLanguage
        })
      });

      if (response.ok) {
        setUserInfo(prev => ({
          ...prev,
          preferred_language: newLanguage
        }));
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  // 페이지가 로드될 때 토큰 검증 및 CSRF 토큰 발급
  useEffect(() => {
    const initializeAuth = async () => {
      // CSRF 토큰 먼저 발급 (보안 API 호출 전 필요)
      await fetchCsrfToken();

      validateToken();
    };

    initializeAuth();
  }, [validateToken]);

  // 페이지 가시성 변경 시 토큰 재검증 (모바일에서 오랫동안 사용하지 않다가 다시 접속할 때)
  useEffect(() => {
    let lastValidationTime = 0;

    const handleVisibilityChange = () => {
      if (!document.hidden && userInfo) {
        const now = Date.now();
        if (now - lastValidationTime > 10000 || lastValidationTime === 0) {
          validateToken();
          lastValidationTime = now;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handleResume = () => {
      const now = Date.now();
      if (now - lastValidationTime > 10000 || lastValidationTime === 0) {
        validateToken();
        lastValidationTime = now;
      }
    };

    if ('onpageshow' in window) {
      window.addEventListener('pageshow', handleResume);
    }

    const handleOnline = () => {
      const now = Date.now();
      if (now - lastValidationTime > 10000 || lastValidationTime === 0) {
        validateToken();
        lastValidationTime = now;
      }
    };

    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if ('onpageshow' in window) {
        window.removeEventListener('pageshow', handleResume);
      }
      window.removeEventListener('online', handleOnline);
    };
  }, [userInfo, validateToken]);

  // userInfo가 변경될 때 localStorage에 저장
  useEffect(() => {
    saveUserInfo(userInfo);
  }, [userInfo]);

  // 테마 변경 함수 (무한루프 방지)
  const updateTheme = (theme) => {
    // 현재 테마와 같으면 무시
    if (theme === userTheme) {
      return;
    }

    setUserTheme(theme);
    changeTheme(theme);
  };

  // 앱 시작 시 테마 초기화 (한 번만 실행)
  useEffect(() => {
    initializeTheme();
  }, []); // 빈 의존성 배열로 한 번만 실행

  return (
    <UserContext.Provider value={{
      userInfo,
      setUserInfo,
      isUserLoading,
      updateLanguage,
      userTheme,
      updateTheme
    }}>
      <AccountSafetyBoundary>{children}</AccountSafetyBoundary>
    </UserContext.Provider>
  );
};
